import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Trophy,
  RefreshCw,
  Loader2,
  Gift,
  Award,
  Bell,
  CheckCircle,
  XCircle,
} from "lucide-react";

// Badges Admin Tab Component
const BadgesAdminTab = ({ token, guests, onNotificationChange, usersPoints, loadingPoints, fetchUsersPoints }) => {
  const [badgeRequests, setBadgeRequests] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState('');
  const [selectedBadgeIds, setSelectedBadgeIds] = useState([]);
  const [badgeSearch, setBadgeSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchBadgeRequests();
    fetchAllBadges();
  }, []);

  const fetchBadgeRequests = async () => {
    try {
      const response = await axios.get(`${API}/passport/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBadgeRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching badge requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBadges = async () => {
    try {
      const response = await axios.get(`${API}/passport/badges`);
      setAllBadges(response.data.badges || []);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.post(`${API}/passport/requests/${requestId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Badge request approved!');
      fetchBadgeRequests();
      if (onNotificationChange) onNotificationChange();
    } catch (error) {
      toast.error('Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.post(`${API}/passport/requests/${requestId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Badge request rejected');
      fetchBadgeRequests();
      if (onNotificationChange) onNotificationChange();
    } catch (error) {
      toast.error('Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  const toggleBadgeSelection = (badgeId) => {
    setSelectedBadgeIds((prev) =>
      prev.includes(badgeId) ? prev.filter((id) => id !== badgeId) : [...prev, badgeId]
    );
  };

  const handleAwardBadges = async () => {
    if (!selectedGuest || selectedBadgeIds.length === 0) {
      toast.error('Pick a guest and at least one badge');
      return;
    }

    setProcessing('award');
    try {
      const response = await axios.post(
        `${API}/admin/passport/award-bulk`,
        { guest_email: selectedGuest, badge_ids: selectedBadgeIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { awarded = [], already_had = [], guest_name } = response.data || {};
      if (awarded.length > 0) {
        const summary = awarded.map((b) => `${b.icon} ${b.name}`).join(', ');
        toast.success(`${awarded.length} badge${awarded.length === 1 ? '' : 's'} given to ${guest_name}: ${summary}`);
      }
      if (already_had.length > 0 && awarded.length === 0) {
        toast.info(`${guest_name} already has all those badges`);
      } else if (already_had.length > 0) {
        toast.message(`${already_had.length} already in their logbook — skipped`);
      }

      setSelectedGuest('');
      setSelectedBadgeIds([]);
      setBadgeSearch('');
      if (fetchUsersPoints) fetchUsersPoints();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to award badges');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Guest Points Leaderboard */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 border border-stone-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[#3D4035] flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#E07A5F]" />
            Guest Logbook Points
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchUsersPoints}
            className="rounded-full"
          >
            <RefreshCw className={`w-4 h-4 ${loadingPoints ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {loadingPoints ? (
          <div className="text-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-[#E07A5F] mx-auto" />
          </div>
        ) : usersPoints && usersPoints.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="text-left border-b border-stone-200">
                  <th className="pb-2 text-xs font-medium text-[#1A1A1A]/60">Rank</th>
                  <th className="pb-2 text-xs font-medium text-[#1A1A1A]/60">Guest</th>
                  <th className="pb-2 text-xs font-medium text-[#1A1A1A]/60 text-center">Points</th>
                  <th className="pb-2 text-xs font-medium text-[#1A1A1A]/60">Badges</th>
                </tr>
              </thead>
              <tbody>
                {usersPoints.map((userPoint, index) => (
                  <tr key={userPoint.email} className="border-b border-stone-100">
                    <td className="py-2">
                      <span className={`w-6 h-6 inline-flex items-center justify-center rounded-full text-xs font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-stone-100 text-stone-500'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        {userPoint.avatar ? (
                          <img src={userPoint.avatar} alt={userPoint.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#F0EDE3] flex items-center justify-center text-xs font-medium">
                            {userPoint.name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-[#1A1A1A]">{userPoint.name}</div>
                          <div className="text-xs text-[#1A1A1A]/50">Lvl {userPoint.level} · {userPoint.level_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 text-center">
                      <span className={`text-lg font-bold ${index === 0 ? 'text-yellow-600' : 'text-[#C85A38]'}`}>
                        {userPoint.total_points}
                      </span>
                    </td>
                    <td className="py-2">
                      <div className="flex gap-1 flex-wrap" data-testid={`admin-badge-row-${userPoint.email || userPoint.name}`}>
                        {userPoint.badges?.map((badge, idx) => (
                          <span key={idx} className="text-lg leading-none" title={badge.name}>{badge.icon}</span>
                        ))}
                        {(!userPoint.badges || userPoint.badges.length === 0) && (
                          <span className="text-xs text-[#1A1A1A]/40 italic">None</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6 text-[#3D4035]/50">
            <Trophy className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No logbook data yet</p>
          </div>
        )}
      </div>

      {/* Award Badges Section */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 border border-stone-100">
        <h3 className="font-medium text-[#3D4035] mb-4 flex items-center gap-2">
          <Gift className="w-5 h-5 text-[#E07A5F]" />
          Award Badges to Guest
        </h3>

        <div className="space-y-4">
          <div>
            <Label className="text-xs sm:text-sm mb-2 block">Select Guest</Label>
            <select
              value={selectedGuest}
              onChange={(e) => setSelectedGuest(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/20"
              data-testid="award-guest-select"
            >
              <option value="">Choose a guest...</option>
              {guests.map((guest) => (
                <option key={guest.email} value={guest.email}>
                  {guest.name} ({guest.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2 gap-2">
              <Label className="text-xs sm:text-sm">
                Pick badges
                {selectedBadgeIds.length > 0 && (
                  <span className="ml-2 text-[#E07A5F] font-medium">({selectedBadgeIds.length} selected)</span>
                )}
              </Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="badges-select-all"
                  onClick={() => {
                    const visibleIds = allBadges
                      .filter((b) => !badgeSearch || b.name.toLowerCase().includes(badgeSearch.toLowerCase()))
                      .map((b) => b.id);
                    setSelectedBadgeIds(visibleIds);
                  }}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-[#F4F1DE] text-[#3D4035] hover:bg-[#E8E4D0] border border-stone-200"
                >
                  Select all
                </button>
                <button
                  type="button"
                  data-testid="badges-clear"
                  onClick={() => setSelectedBadgeIds([])}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-white text-[#3D4035]/60 hover:bg-stone-50 border border-stone-200"
                >
                  Clear
                </button>
              </div>
            </div>
            <input
              type="text"
              value={badgeSearch}
              onChange={(e) => setBadgeSearch(e.target.value)}
              placeholder="Filter badges by name…"
              className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/20"
              data-testid="badges-search"
            />
            <div
              className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-1.5 max-h-72 overflow-y-auto pr-1"
              data-testid="badges-multiselect-grid"
            >
              {allBadges
                .filter((b) => !badgeSearch || b.name.toLowerCase().includes(badgeSearch.toLowerCase()))
                .map((badge) => {
                  const selected = selectedBadgeIds.includes(badge.id);
                  return (
                    <button
                      key={badge.id}
                      type="button"
                      onClick={() => toggleBadgeSelection(badge.id)}
                      data-testid={`badge-option-${badge.id}`}
                      title={badge.description}
                      className={`flex flex-col items-center text-center p-1.5 rounded-lg border transition-all ${
                        selected
                          ? 'bg-[#E07A5F]/10 border-[#E07A5F] ring-1 ring-[#E07A5F]/30'
                          : 'bg-[#F8F6F3] border-transparent hover:border-stone-300'
                      }`}
                    >
                      <span className="text-xl leading-none">{badge.icon}</span>
                      <span className="mt-0.5 text-[10px] font-medium text-[#3D4035] leading-tight line-clamp-2">{badge.name}</span>
                      <span className="text-[9px] text-[#E07A5F] mt-0.5">+{badge.points}</span>
                    </button>
                  );
                })}
            </div>
          </div>

          <div>
            <Button
              onClick={handleAwardBadges}
              disabled={processing === 'award' || !selectedGuest || selectedBadgeIds.length === 0}
              className="w-full sm:w-auto bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-xl"
              data-testid="award-badges-btn"
            >
              {processing === 'award' ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Award className="w-4 h-4 mr-2" />
              )}
              {selectedBadgeIds.length > 1
                ? `Award ${selectedBadgeIds.length} badges`
                : selectedBadgeIds.length === 1
                ? 'Award badge'
                : 'Award badges'}
            </Button>
          </div>
        </div>
      </div>

      {/* Pending Badge Requests */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 border border-stone-100">
        <h3 className="font-medium text-[#3D4035] mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" />
          Pending Badge Requests
          {badgeRequests.length > 0 && (
            <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
              {badgeRequests.length}
            </span>
          )}
        </h3>

        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F] mx-auto" />
          </div>
        ) : badgeRequests.length === 0 ? (
          <div className="text-center py-8 text-[#3D4035]/50">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500/50" />
            <p>No pending badge requests</p>
          </div>
        ) : (
          <div className="space-y-3">
            {badgeRequests.map((request) => (
              <div 
                key={request.id}
                className="flex items-center gap-4 p-4 bg-[#F8F6F3] rounded-xl"
              >
                <span className="text-3xl">{request.badge_icon}</span>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#3D4035]">{request.guest_name}</span>
                    <span className="text-xs text-[#3D4035]/50">wants</span>
                    <span className="font-medium text-[#E07A5F]">{request.badge_name}</span>
                  </div>
                  {request.reason && (
                    <p className="text-sm text-[#3D4035]/60 mt-1 truncate">"{request.reason}"</p>
                  )}
                  <p className="text-xs text-[#3D4035]/40 mt-1">
                    {new Date(request.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleApprove(request.id)}
                    disabled={processing === request.id}
                    className="bg-green-500 hover:bg-green-600 text-white rounded-full"
                  >
                    {processing === request.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleReject(request.id)}
                    disabled={processing === request.id}
                    className="rounded-full border-red-300 text-red-500 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Badges Reference */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 border border-stone-100">
        <h3 className="font-medium text-[#3D4035] mb-4">All Available Badges</h3>
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-1.5">
          {allBadges.map((badge) => (
            <div 
              key={badge.id}
              className="p-1.5 bg-[#F8F6F3] rounded-lg text-center"
              title={badge.description}
            >
              <span className="text-xl block leading-none">{badge.icon}</span>
              <span className="mt-0.5 text-[10px] font-medium text-[#3D4035] block leading-tight line-clamp-2">{badge.name}</span>
              <span className="text-[9px] text-[#E07A5F]">+{badge.points}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BadgesAdminTab;
